import React from 'react';
import Plot from 'react-plotly.js';
import { Card, Row, Col, Spinner, Alert, Table } from 'react-bootstrap';
import {
  CumulativeHistoryData,
  useGetCumulativeHistoryQuery,
  useGetMixComparisonQuery,
} from '../../store/api/analysisApi';
import * as Plotly from 'plotly.js';

const HistoryExplorer: React.FC = () => {
  const { data: historyData, isLoading: loadingHistory, error: historyError } =
    useGetCumulativeHistoryQuery();
  const { data: mixData, isLoading: loadingMix, error: mixError } = useGetMixComparisonQuery();

  if (loadingHistory || loadingMix) return <div className="text-center p-5"><Spinner animation="border" /></div>;
  if (historyError || mixError || !historyData || !historyData.stages || !mixData) {
    return <Alert variant="danger">{'Failed to fetch historical data.'}</Alert>;
  }

  // No data uploaded yet
  if (historyData.stages.length === 0) {
    return <Alert variant="info">No data available yet.</Alert>;
  }

  const historyStages = historyData.stages.map((d) => d.stageName.replace(/_/g, ' '));
  const globalMeanZHistory = historyData.stages.map((d) => d.globalMeanZScore);

  const allBatches: string[] = Array.from(
    new Set<string>(
      historyData.stages.flatMap((d) => Object.keys(d.top30PercentComposition))
    )
  ).sort();
  
  const top30StackedBarData: Plotly.Data[] = allBatches.map((batchId): Plotly.Data => ({
    x: historyStages,
    y: historyData.stages.map((d) => (d.top30PercentComposition[batchId] || 0) * 100),
    name: batchId,
    type: 'bar'
  }));

  const finalComposition = historyData.stages.at(-1)?.top30PercentComposition ?? {};
  const top30PieChartData: Plotly.Data[] = [{
    type: 'pie', labels: Object.keys(finalComposition), values: Object.values(finalComposition), hole: .4, textinfo: 'label+percent', insidetextorientation: 'radial'
  }];

  const mixPlotData: Plotly.Data[] = Object.entries(mixData.batchDistributions ?? {}).map(([name, values]): Plotly.Data => ({
    type: 'violin', x: values as number[], name: name.replace(/_/g, ' '), side: 'positive', orientation: 'h', points: false, line: { color: '#0A6353' } // Color was removed in a previous step, restoring a default
  }));
  
  const persistenceScores = historyData.persistence.map((p) => p.persistenceScore);

  return (
    <Row className="g-4">
      <Col xl={12}><Card><Card.Body>
        <Card.Title><h4>Global Z-Score Evolution</h4></Card.Title>
        <Card.Text>How the population&apos;s average Z-Score changes as more data is added.</Card.Text>
        <Plot
          data={[{ x: historyStages, y: globalMeanZHistory, type: 'scatter', mode: 'lines+markers' }]}
          layout={{ title: { text: '' }, yaxis: { title: { text: 'Mean Z-Score' } } }}
          style={{ width: '100%', height: '400px' }}
        />
      </Card.Body></Card></Col>

      <Col xl={12}><Card><Card.Body>
        <Card.Title><h4>&quot;Sticky&quot; Subjects in Top 30% Cohort</h4></Card.Title>
        <Card.Text>This table shows subjects who have ever been in the top 30% for high Z-scores, ranked by how often they appear there. Consistent subjects are the primary targets for intervention.</Card.Text>
        <div style={{ flex: '1 1 auto', overflowY: 'auto', maxHeight: '400px' }}>
          <Table striped bordered hover size="sm">
            <thead><tr><th>Rank</th><th>Subject ID</th><th>Persistence (% of time in Top 30)</th><th>Final Z-Score</th></tr></thead>
            <tbody>
              {historyData.persistence.slice(0, 20).map((p, i) => (
                <tr key={p.subjectId}>
                  <td>{i + 1}</td><td>{p.subjectId}</td><td>{p.persistenceScore.toFixed(0)}%</td><td>{p.finalZScore.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <hr/>
        <Card.Subtitle className="mb-2 text-muted">Persistence Score Distribution</Card.Subtitle>
        <Plot
          data={[{ x: persistenceScores, type: 'histogram', marker: {color: '#636EFA'} }]}
          layout={{ title: { text: '' }, height: 250, xaxis: {title: {text: 'Persistence Score (%)'}}, yaxis: {title: {text: 'Number of Subjects'}} }}
          style={{ width: '100%' }}
        />
      </Card.Body></Card></Col>
      
      <Col xl={6}><Card className="h-100"><Card.Body>
        <Card.Title><h4>Top 30% Cohort Evolution</h4></Card.Title>
        <Card.Text>Shows the percentage contribution of each batch to the top 30% cohort at each cumulative stage.</Card.Text>
        <Plot
          data={top30StackedBarData}
          layout={{ title: { text: '' }, height: 450, barmode: 'stack', yaxis: { title: { text: 'Composition (%)' }, ticksuffix: '%' }}}
          style={{ width: '100%' }}
        />
      </Card.Body></Card></Col>

      <Col xl={6}><Card className="h-100"><Card.Body>
        <Card.Title><h4>Final Composition Snapshot</h4></Card.Title>
        <Card.Text>A snapshot of which batches make up the top 30% in the final, complete dataset.</Card.Text>
        <Plot
          data={top30PieChartData}
          layout={{ title: { text: '' }, height: 450, showlegend: true, margin: { t: 40, b: 40, l: 40, r: 40 } }}
          style={{ width: '100%' }}
        />
      </Card.Body></Card></Col>

      <Col xl={12}><Card><Card.Body>
        <Card.Title><h4>MIX Subject vs. Batches</h4></Card.Title>
        <Card.Text>Comparing reference subject &apos;{mixData.mixSubjectId}&apos; against each batch&apos;s distribution.</Card.Text>
        <Plot
          data={mixPlotData}
          layout={{
            title: { text: '' },
            showlegend: false,
            height: Math.max(400, Object.keys(mixData.batchDistributions ?? {}).length * 50 + 100),
            xaxis: { title: { text: "Z-Score" }},
            shapes: typeof mixData.mixSubjectZScore === 'number'
              ? [{ type: 'line', xref: 'x', yref: 'paper', x0: mixData.mixSubjectZScore, x1: mixData.mixSubjectZScore, y0: 0, y1: 1, line: { color: '#d9534f', width: 3, dash: 'dash' }}]
              : [],
            annotations: typeof mixData.mixSubjectZScore === 'number'
              ? [{ x: mixData.mixSubjectZScore, y: 1.05, yref: 'paper', text: `MIX Z-Score: ${mixData.mixSubjectZScore.toFixed(2)}`, showarrow: false}]
              : []
          }}
          style={{ width: '100%' }}
        />
      </Card.Body></Card></Col>
    </Row>
  );
};

export default HistoryExplorer;
